// assets/js/utils/guest-counts.js
/**
 * Guest-count resolution — the SINGLE owner of "how many people are in this
 * party?".
 *
 * A response can carry that fact in three overlapping shapes:
 *
 *   adultCount / childrenCount        written by guest form submissions
 *   numberOfAdults / numberOfChildren legacy, and still written by some
 *                                     server paths for backward compatibility
 *   guestCount                        the single-number form mode, and the
 *                                     number a host edits on a party they
 *                                     entered by hand
 *
 * Before this module every surface re-derived the answer its own way, so the
 * same row could report a different size in the dashboard table, the CSV
 * export, check-in, seating and a wallet pass. The specific failure that
 * exposed it: the one-tap decline and manual-response paths stamped a party of
 * ONE under the legacy names while the host raised the real size in
 * guestCount, so a party of three read as "1 adult" anywhere that trusted the
 * legacy pair — including a child who existed only in individualNames.
 *
 * Anything that needs a headcount calls resolveGuestCounts(). Nothing else
 * should read these fields directly.
 */

/**
 * @param {object} response A response doc (or a submission payload).
 * @returns {{adults: (number|null), children: (number|null), total: number, hasSplit: boolean}}
 *   `hasSplit` is false when the form only ever collected one number, in which
 *   case adults/children are null and only `total` is meaningful — callers
 *   must not present "N adults, 0 children" for those.
 */
function resolveGuestCounts(response) {
    const r = response || {};
    // Never below zero: a party is people, and a hand-made request sending
    // {adultCount: 10, childrenCount: -7} read as 3 and passed a cap of 3
    // (audit 2026-10-27). A negative guestCount resolves like 0, to 1.
    const int = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) ? Math.max(0, n) : undefined; };

    const names = Array.isArray(r.individualNames) ? r.individualNames : [];
    const childNames = names.filter(n => n && n.guestType === 'child').length;
    const guestCount = int(r.guestCount);

    // 1. The modern pair. The form wrote it, so it is authoritative — including
    //    a deliberate 0 children.
    const modernAdults = int(r.adultCount);
    const modernChildren = int(r.childrenCount);
    if (modernAdults !== undefined || modernChildren !== undefined) {
        const adults = Math.max(1, modernAdults || 0);
        const children = modernChildren || 0;
        return { adults, children, total: adults + children, hasSplit: true };
    }

    // 2. The legacy pair — trusted only while it still agrees with the numbers
    //    a human actually maintains (guestCount, and the names they typed).
    //    When it doesn't, it is the frozen party-of-one described above:
    //    rebuild the split, taking children from the individualNames flags,
    //    which is the only place a child is recorded on those rows.
    const legacyAdults = int(r.numberOfAdults);
    const legacyChildren = int(r.numberOfChildren);
    if (legacyAdults !== undefined || legacyChildren !== undefined) {
        const adults = Math.max(1, legacyAdults || 0);
        const children = legacyChildren || 0;
        const total = adults + children;
        const maintained = Math.max(guestCount || 0, names.length);
        if (maintained > total) {
            const resolvedChildren = Math.max(children, childNames);
            return {
                adults: Math.max(1, maintained - resolvedChildren),
                children: resolvedChildren,
                total: maintained,
                hasSplit: true
            };
        }
        return { adults, children, total, hasSplit: true };
    }

    // 3. Single-number form: a total, and no split to report.
    return { adults: null, children: null, total: guestCount || 1, hasSplit: false };
}

/**
 * Party size as a plain number — the common case. Non-attending responses
 * bring nobody, so they resolve to 0 unless `countNonAttending` is set (the
 * edit modal and the invite-only allowance check both need the stored party of
 * a "no" row).
 *
 * @param {object} response
 * @param {{countNonAttending?: boolean}} [opts]
 * @returns {number}
 */
function resolvePartySize(response, opts) {
    const attending = String((response || {}).attending || '').toLowerCase();
    const counts = ['yes', 'attending', 'maybe', 'pending'].includes(attending);
    if (!counts && !(opts && opts.countNonAttending)) return 0;
    return resolveGuestCounts(response).total;
}

const GuestCounts = { resolveGuestCounts, resolvePartySize };

// Dual-environment, matching assets/js/utils/validation.js: the browser gets
// the globals, the server requires this same file. One implementation — a
// second copy is how the surfaces drifted apart in the first place.
if (typeof window !== 'undefined') {
    window.GuestCounts = GuestCounts;
    window.resolveGuestCounts = resolveGuestCounts;
    window.resolvePartySize = resolvePartySize;
}
if (typeof module !== 'undefined' && module.exports) module.exports = GuestCounts;
